import { DataPeriod } from '@prisma/client';
import { Avatar, AvatarFallback, AvatarImage, Card, Skeleton } from '@zond/ui';
import { cn } from '@zond/utils';
import { endOfMonth, endOfWeek, format, isValid, parse, parseISO, startOfMonth, startOfWeek } from 'date-fns';
import { ChevronRight, Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { LOCAL_STORAGE_KEYS } from '../../../constants';
import { useStore } from '../../../store';

interface Report {
  id: string | number;
  projectId: number;
  user: {
    id: number;
    email: string;
    firstname: string;
    lastname: string;
    displayname: string;
    avatar: string | null;
    avatarBackground: string;
    avatarEmoji: string;
  };
  type: DataPeriod;
  date: string;
}

interface RecentReportsSectionProps {
  reports: Report[] | null | undefined;
  limit?: number;
  isLoading?: boolean;
}

function RecentReportsSkeleton() {
  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-5 w-20" />
      </div>
      <div className="space-y-3 min-h-[300px]">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 p-2">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 min-w-0 max-w-[calc(100%-160px)]">
              <div className="flex flex-col gap-1">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
              </div>
            </div>
            <div className="text-right flex-shrink-0 w-[140px]">
              <Skeleton className="h-4 w-16 ml-auto mb-1" />
              <Skeleton className="h-3 w-24 ml-auto" />
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function RecentReportsSection({ reports, limit = 5, isLoading }: RecentReportsSectionProps) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const selectedWorkspaceId = useStore((state) => state.getSelectedWorkspaceId());
  const selectedProjectId = localStorage.getItem(LOCAL_STORAGE_KEYS.SELECTED_PROJECT_ID);

  const handleViewAll = () => {
    if (selectedProjectId) {
      navigate(`/${selectedWorkspaceId}/reports/${selectedProjectId}`);
    } else {
      navigate(`/${selectedWorkspaceId}/reports`);
    }
  };

  const formatDate = (date: string, type: DataPeriod) => {
    let parsedDate;

    try {
      parsedDate = parseISO(date);

      if (!isValid(parsedDate)) {
        parsedDate = new Date(date);
      }

      if (!isValid(parsedDate)) {
        return date;
      }
    } catch (error) {
      console.error('Error parsing date:', error);
      return date;
    }

    switch (type) {
      case DataPeriod.Daily:
        return format(parsedDate, 'MMM d, yyyy');

      case DataPeriod.Weekly:
        const weekStart = startOfWeek(parsedDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(parsedDate, { weekStartsOn: 1 });

        if (format(weekStart, 'MMM') === format(weekEnd, 'MMM')) {
          return `${format(weekStart, 'MMM d')}-${format(weekEnd, 'd, yyyy')}`;
        }
        return `${format(weekStart, 'MMM d')}-${format(weekEnd, 'MMM d, yyyy')}`;

      case DataPeriod.Monthly:
        return format(parsedDate, 'MMM yyyy');

      default:
        return format(parsedDate, 'MMM d, yyyy');
    }
  };

  const formatPeriodType = (type: DataPeriod): string => {
    switch (type) {
      case DataPeriod.Daily:
        return t('reports.period.daily');
      case DataPeriod.Weekly:
        return t('reports.period.weekly');
      case DataPeriod.Monthly:
        return t('reports.period.monthly');
      default:
        return type;
    }
  };

  const getInitials = (firstname: string, lastname: string) => {
    return `${firstname?.[0] || ''}${lastname?.[0] || ''}`.toUpperCase();
  };

  if (isLoading || reports === null || reports === undefined) {
    return <RecentReportsSkeleton />;
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-semibold">{t('reports.title')}</h3>
          <p className="text-sm text-muted-foreground">{t('reports.subtitle')}</p>
        </div>
        <button
          onClick={handleViewAll}
          className="text-sm text-muted-foreground hover:text-primary flex items-center"
        >
          {t('reports.viewAll')}
          <ChevronRight className="h-4 w-4 ml-1" />
        </button>
      </div>
      <div className="space-y-1 min-h-[200px]">
        {reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center w-full h-[200px] p-6 rounded-lg">
            <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
              <Inbox className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex flex-col items-center text-center">
              <h2 className="text-lg font-semibold mb-2">{t('reports.empty.title')}</h2>
              <p className="text-sm text-muted-foreground max-w-[300px]">{t('reports.empty.description')}</p>
            </div>
          </div>
        ) : (
          reports.slice(0, limit).map((report) => (
            <Link
              key={report.id}
              to={`/${selectedWorkspaceId}/reports/${report.projectId}/user/${report.user.id}?range=${report.type}&date=${report.date}`}
              className={cn(
                'flex items-center gap-3 p-2 rounded-md transition-colors',
                'hover:bg-muted/50 focus:bg-muted/50',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
              )}
            >
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={report.user.avatar || ''} alt={report.user.displayname} />
                <AvatarFallback>{getInitials(report.user.firstname, report.user.lastname)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 max-w-[calc(100%-160px)]">
                <div className="flex flex-col">
                  <h4 className="font-medium truncate" title={report.user.displayname}>
                    {report.user.displayname}
                  </h4>
                  <p className="text-sm text-muted-foreground truncate" title={report.user.email}>
                    {report.user.email}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0 w-[140px]">
                <p className="text-sm font-medium">{formatPeriodType(report.type)}</p>
                <p className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatDate(report.date, report.type)}
                </p>
              </div>
            </Link>
          ))
        )}
      </div>
    </Card>
  );
}
